import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Divider,
} from '@mui/material';
import { useRecoilState, useRecoilValue } from 'recoil';
import { botFlowDataAtom, entityPreviewDataAtom, flowObjAtom } from '../../../Services/_states/index.js';
import EntityDropdown from '../../Shared/EntityDropdown/EntityDropdown.tsx';
import { EntitySetting, EntityValidationErrors, Node } from '../../../interfaces/Flowbuilder.interface.ts';
import SearchableDropdown from '../Common/SearchableDropdown/SearchableDropdown.tsx';
import { EntityActions } from '../../../Services/_actions/Entity/entity.actions.ts';
import { getAllParentNodeResults } from '../helper.tsx';
import EntityQueryParameters from './EntityQueryParameters.tsx';

interface EntityDataSettingProps {
  data: Node;
  entitySetting?: EntitySetting;
  setEntitySetting?: any;
  entitySettingErrors?: EntityValidationErrors;
}

const EntityDataSetting: React.FC<EntityDataSettingProps> = ({ data, entitySetting, setEntitySetting, entitySettingErrors }) => {
  const botFlowData = useRecoilValue(botFlowDataAtom);
  const flowObj = useRecoilValue(flowObjAtom);
  const entityActions = EntityActions();
  const parentResults = getAllParentNodeResults(data?.id ?? null, flowObj);
  const [entityPreviewData, setEntityPreviewData] = useRecoilState(entityPreviewDataAtom);
  const currentEntityFormFields =
    entityPreviewData?.[data?.id]?.form_fields
      ?.filter((item) => !['file', 'video', 'image', 'audio', 'document']?.includes(item?.type)) // keep only valid ones
      .map((item) => ({ name: item?.name, label: item?.label }))
    ?? [];

  const getEntityFormById = async (entity) => {
    try {
      if (!entity?._id && entityPreviewData?.[data?.id]) return;
      if (entityPreviewData?.[data?.id]) return;
      const response = await entityActions?.getFormById(entity?._id);
      console.log("Entity Form Response:", response);
      if (response?.status === 200 && response?.data?.items) {
        const form = response?.data?.items;
        setEntityPreviewData(prev => ({
          ...prev,
          [data?.id]: form
        }));
      }
    } catch (error) {
      console.log(error)
    }
  }

  const handleSelectParentResult = async (e) => {
    if (e?.node_id) {
      const nodes = flowObj?.nodes ?? [];
      const nodeData: Node | undefined = nodes.find((node) => node?.id === e?.node_id);
      if (nodeData?.data?.entity_setting?.entity?._id) {
        const entity = nodeData?.data?.entity_setting?.entity;
        try {
          if (!entity?._id) return;
          if (entityPreviewData?.[e?.node_id]) return;
          const response = await entityActions?.getFormById(entity?._id);
          if (response?.status === 200 && response?.data?.items) {
            const form = response?.data?.items;
            setEntityPreviewData({ ...entityPreviewData, [e?.node_id]: form });
          }
        } catch (error) {
          console.log(error)
        }
      }
    }
  }

  const handleSetMatchedWithNull = (entity: any) => {
    const currentQueryParams = [...(entitySetting?.query_parameter || [])];
    const updatedQueryParams = currentQueryParams.map((param) => ({ ...param, matched_with: null }));
    setEntitySetting({ ...entitySetting, query_parameter: updatedQueryParams, retrive_fields: [], entity: entity });
  }

  useEffect(() => {
    if (entitySetting?.entity)
      getEntityFormById(entitySetting?.entity);
  }, [])


  return (
    <>
      <EntityDropdown
        label={<Typography>Select Entity <span style={{ color: "red" }}>*</span></Typography>}
        uniqueId={data?.id}
        value={entitySetting?.entity ?? null}
        organisation_id={botFlowData?.company_id ?? ''}
        onChange={(e) => {
          let entity: any = null;
          if (e) {
            entity = { _id: e?._id ?? "", name: e?.name ?? "" };
          }
          getEntityFormById(entity);
          // setEntityPreviewData({ ...entityPreviewData, [data?.id]: e });
          handleSetMatchedWithNull(entity);
        }}
      />
      {entitySettingErrors && entitySettingErrors["entity"] && (
        <Typography color="error" variant="caption">
          {entitySettingErrors["entity"]}
        </Typography>)
      }

      <Box>
        <Divider sx={{ mb: 2 }} />
        <EntityQueryParameters
          group={entitySetting}
          onGroupChange={setEntitySetting}
          groupLabel="Where"
          groupColor="primary"
          parentResults={parentResults}
          currentEntityFormFields={currentEntityFormFields}
          handleSelectParentResult={handleSelectParentResult}
          entityPreviewData={entityPreviewData}
        />
      </Box>
      <Box mt={2} mb={1}>
        {/* <Typography variant="subtitle2" gutterBottom>
          Retrieve Parameters
        </Typography> */}
        <SearchableDropdown
          options={currentEntityFormFields}
          value={
            currentEntityFormFields?.filter((opt) =>
              (entitySetting?.retrive_fields ?? []).includes(opt?.name)
            )
          }
          onChange={(selectedOptions) => {
            const selectedKeys = (selectedOptions ?? []).map((opt) => opt.name);
            setEntitySetting({ ...entitySetting, retrive_fields: selectedKeys });
          }}
          label={<Typography>Retrieve Entity Field <span style={{ color: "red" }}>*</span></Typography>}
          multiSelect={true}
          valueKey="name"
          getOptionDisabled={(option) => (entitySetting?.retrive_fields ?? [])?.length > 1}
        />
        {entitySettingErrors && entitySettingErrors["retrive_fields"] && (
          <Typography color="error" variant="caption">
            {entitySettingErrors["retrive_fields"]}
          </Typography>)
        }
      </Box>
    </>
  );
};

export default EntityDataSetting;
